import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "@/api/auth.ts";
import { googleApi } from "@/api/imports.ts";
import { useAuthStatus } from "./session.ts";
import { Alert, Button, CodeInput, Field, Input, PageTitle } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { AuthLayout } from "./AuthLayout.tsx";
import { useSetAuthUser } from "./session.ts";

export function LoginPage() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const nav = useNavigate();
  const setUser = useSetAuthUser();
  const [form, setForm] = useState({ email: "", password: "" });
  const [totpCode, setTotpCode] = useState("");
  const params = new URLSearchParams(location.search);
  const pendingGoogle = params.get("pending");
  const googleError = params.get("google_error");
  const [step, setStep] = useState<"credentials" | "totp">(pendingGoogle ? "totp" : "credentials");
  const status = useAuthStatus();
  const gTotp = useMutation({
    mutationFn: () => googleApi.totp(pendingGoogle!, totpCode),
    onSuccess: (res) => { setUser(res.user); void nav({ to: "/" }); },
  });

  const m = useMutation({
    mutationFn: () => authApi.login({ email: form.email, password: form.password, ...(step === "totp" ? { totpCode } : {}) }),
    onSuccess: (res) => {
      if (res.status === "ok") {
        setUser(res.user);
        const back = sessionStorage.getItem("codvero.afterLogin");
        if (back?.startsWith("/oauth/consent")) { sessionStorage.removeItem("codvero.afterLogin"); location.href = back; return; }
        void nav({ to: "/" });
      } else if (res.status === "totp_required") {
        setStep("totp");
      } else {
        void nav({ to: "/verify", search: { pending: res.pendingUserId, sentTo: form.email } });
      }
    },
  });

  return (
    <AuthLayout>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (pendingGoogle) gTotp.mutate();
          else m.mutate();
        }}
      >
        {step === "credentials" ? (
          <>
            <PageTitle title={t("login.title")} />
            {m.error && <Alert tone="danger">{toMsg(m.error)}</Alert>}
            {googleError && <Alert tone="danger">{googleError === "disabled" ? t("login.googleDisabled") : googleError === "denied" ? t("login.googleDenied") : t("login.googleFailed", { reason: googleError })}</Alert>}
            {status.data?.googleSignIn && (
              <>
                <a href={googleApi.signInUrl} className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-border bg-surface text-sm font-medium hover:bg-surface-2">
                  <svg viewBox="0 0 24 24" className="size-4" aria-hidden><path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.8-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1C3.3 21.3 7.3 24 12 24z"/><path fill="#FBBC05" d="M5.3 14.3c-.3-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l4-3.1z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-5 6.7-5z"/></svg>
                  {t("login.google")}
                </a>
                <div className="flex items-center gap-3 text-xs text-fg-muted"><span className="h-px flex-1 bg-border" />{t("login.or")}<span className="h-px flex-1 bg-border" /></div>
              </>
            )}
            <Field label={t("common.email")}>
              <Input type="email" dir="ltr" required autoFocus autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label={t("common.password")}>
              <Input type="password" dir="ltr" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
            <Button type="submit" className="w-full" loading={m.isPending}>{t("login.submit")}</Button>
            <p className="text-center text-sm text-fg-muted">
              {t("login.noAccount")} <Link to="/register" className="text-accent hover:underline">{t("login.register")}</Link>
            </p>
          </>
        ) : (
          <>
            <PageTitle title={t("login.totpTitle")} subtitle={t("login.totpSubtitle")} />
            {(m.error || gTotp.error) && <Alert tone="danger">{toMsg(m.error ?? gTotp.error)}</Alert>}
            <Field label={t("login.totpCode")}>
              <CodeInput autoFocus required value={totpCode} onChange={setTotpCode} />
            </Field>
            <Button type="submit" className="w-full" loading={m.isPending || gTotp.isPending} disabled={totpCode.length !== 6}>{t("common.continue")}</Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => { setStep("credentials"); setTotpCode(""); m.reset(); if (pendingGoogle) history.replaceState(null, "", "/login"); }}>{t("common.back")}</Button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
